/**
 * Presentational component for the browser-extension pairing section on the Profile page.
 */

import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import { Button, SpinningLoader, TextInput } from "risa-oasis-ui_v2";

/** Props for {@link BrowserExtensionSection}. */
interface BrowserExtensionSectionProps {
  /** Stable key to remount the device-id input when extension reports a new id */
  deviceIdInputKey: number;
  /** Current device id entered or reported by the extension */
  deviceId: string;
  /** Whether the extension is currently paired and logged in */
  extensionConnected: boolean;
  /** Whether a pairing-code request is in flight */
  connectingExtension: boolean;
  /** Generated pairing code shown after a successful connect request */
  pairingCode: string;
  /** Called when the device-id input value changes */
  onDeviceIdChange: (value: string) => void;
  /** Called when the user clicks Disconnect */
  onDisconnect: () => void;
  /** Called when the user clicks Connect to Extension */
  onConnect: () => void;
  /** Called when the user clicks Copy Code */
  onCopyPairingCode: () => void;
}

/**
 * Renders the browser-extension pairing UI: device id, connect/reconnect, pairing code,
 * and a Connected/Disconnected status tag beside the section heading.
 */
export function BrowserExtensionSection({
  deviceIdInputKey,
  deviceId,
  extensionConnected,
  connectingExtension,
  pairingCode,
  onDeviceIdChange,
  onDisconnect,
  onConnect,
  onCopyPairingCode,
}: BrowserExtensionSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center gap-2">
        <div className="text-base font-semibold">Browser Extension</div>
        {extensionConnected ? (
          <Tags
            label="Connected"
            backgroundColor="#E6F3F0"
            textColor="#166534"
            showBorder={true}
          />
        ) : (
          <Tags
            label="Disconnected"
            backgroundColor="#F3F4F6"
            textColor="#6B7280"
            showBorder={true}
          />
        )}
      </div>
      <div className="flex flex-row items-end gap-3">
        <div className="flex-1">
          <TextInput
            key={deviceIdInputKey}
            id="extension-device-id"
            label="Device ID"
            placeholder="Paste the device ID from the extension popup"
            defaultValue={deviceId}
            onChange={(data) => {
              onDeviceIdChange(data.value);
            }}
          />
        </div>
        {extensionConnected ? (
          <Button
            disabled={false}
            buttonType="secondary"
            size="medium"
            onClick={onDisconnect}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            disabled={connectingExtension || deviceId.trim() === ""}
            buttonType="primary"
            size="medium"
            onClick={onConnect}
          >
            Connect to Extension
            {connectingExtension && <SpinningLoader />}
          </Button>
        )}
      </div>
      {pairingCode && (
        <div className="flex flex-row items-center gap-3">
          <div className="rounded-md border border-gray-300 bg-gray-50 px-4 py-2 font-mono text-lg tracking-widest">
            {pairingCode}
          </div>
          <Button
            disabled={false}
            buttonType="secondary"
            size="medium"
            onClick={onCopyPairingCode}
          >
            Copy Code
          </Button>
        </div>
      )}
    </div>
  );
}
